<template>
  <div class="vdp" role="group" :aria-label="t('player.info.information')">

    <!-- ── Information ── -->
    <div class="vdp__section">
      <h4 v-if="title" class="vdp__title">{{ title }}</h4>

      <div v-if="formattedDuration" class="vdp__badges">
        <span class="vdp__badge">
          <svg class="vdp__badge-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
          </svg>
          {{ formattedDuration }}
        </span>
      </div>

      <div v-if="description" class="vdp__description-wrapper">
        <div
          ref="descriptionRef"
          class="vdp__description"
          :class="{ 'vdp__description--expanded': isDescriptionExpanded }"
        >{{ description }}</div>
        <button
          v-if="isDescriptionOverflowing"
          class="vdp__show-more"
          @click="isDescriptionExpanded = !isDescriptionExpanded"
        >
          {{ isDescriptionExpanded ? t('player.info.showLess') : t('player.info.showMore') }}
        </button>
      </div>
      <p v-else class="vdp__no-description">{{ t('player.info.noDescription') }}</p>

      <!-- Public metadata fields -->
      <dl v-if="publicMetadata.length" class="vdp__metadata">
        <div v-for="(item, index) in publicMetadata" :key="index" class="vdp__metadata-item">
          <dt class="vdp__metadata-label">{{ item.label }}</dt>
          <dd class="vdp__metadata-value">{{ item.value }}</dd>
        </div>
      </dl>
    </div>

    <!-- ── Transcript shortcut ── -->
    <div v-if="hasSubtitles" class="vdp__section">
      <button
        class="vdp__card"
        @click="player.togglePanel(PanelType.TRANSCRIPT)"
      >
        <span class="vdp__card-icon-wrapper">
          <svg class="vdp__card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
          </svg>
        </span>
        <span class="vdp__card-text">
          <span class="vdp__card-label">{{ t('player.panels.transcript.title') }}</span>
          <span class="vdp__card-description">{{ t('player.info.viewTranscript') }}</span>
        </span>
        <svg class="vdp__card-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>

    <!-- ── Downloads ── -->
    <div v-if="hasDownloads" class="vdp__section">
      <h3 class="vdp__section-title">{{ t('player.info.downloads') }}</h3>

      <!-- Source video download -->
      <a
        v-if="sourceDownloadUrl"
        :href="sourceDownloadUrl"
        class="vdp__card"
        :aria-label="t('player.info.downloadSource')"
      >
        <span class="vdp__card-icon-wrapper">
          <svg class="vdp__card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
          </svg>
        </span>
        <span class="vdp__card-text">
          <span class="vdp__card-label">{{ t('player.info.sourceVideo') }}</span>
          <span class="vdp__card-description">{{ t('player.info.downloadSource') }}</span>
        </span>
        <svg class="vdp__card-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
        </svg>
      </a>

      <!-- Linked files -->
      <a
        v-for="file in linkedFiles"
        :key="file.uid"
        :href="file.fileUrl?.url"
        class="vdp__card"
        :class="{ 'vdp__card--disabled': !file.fileUrl }"
        :download="file.fileUrl ? '' : undefined"
        :aria-label="`${t('player.info.download')} ${file.name}`"
        @click="!file.fileUrl && $event.preventDefault()"
      >
        <span class="vdp__card-icon-wrapper">
          <svg class="vdp__card-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path v-if="file.type === 'document'" d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            <path v-else-if="file.type === 'image'" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            <path v-else-if="file.type === 'audio'" d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/>
            <path v-else d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
          </svg>
        </span>
        <span class="vdp__card-text">
          <span class="vdp__card-label vdp__card-label--truncate">{{ file.name }}</span>
          <span class="vdp__card-description">{{ t('player.info.download') }}</span>
        </span>
        <svg v-if="file.fileUrl" class="vdp__card-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
        </svg>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { useVideoI18n } from '../../i18n'
import { PanelType } from '../../types'
import { formatDuration } from '../../utils/time'
import type { LinkedFile, PublicMetadata } from '../../../types/asset'

interface Props {
  player: UseVideoPlayerReturn
}

const props = defineProps<Props>()
const { t } = useVideoI18n()

const currentItem = computed(() => props.player.currentItem.value)
const currentPlaybackInfo = computed(() => props.player.currentPlaybackInfo.value)

const title = computed(() => {
  return currentItem.value?.customTitle || currentItem.value?.asset.name || ''
})

const description = computed(() => {
  return currentItem.value?.customDescription || currentItem.value?.asset.description || ''
})

const formattedDuration = computed(() => {
  const duration = currentItem.value?.asset.duration
  if (!duration || duration <= 0) return ''
  return formatDuration(duration)
})

const publicMetadata = computed<PublicMetadata[]>(() => currentItem.value?.asset.publicMetadata || [])
const linkedFiles = computed<LinkedFile[]>(() => currentPlaybackInfo.value?.linkedFiles || [])
const sourceDownloadUrl = computed(() => props.player.sourceDownloadUrl.value)
const hasDownloads = computed(() => props.player.hasDownloads.value)

const hasSubtitles = computed(() => props.player.availableSubtitleTracks.value.length > 0)

// Collapsible description
const isDescriptionExpanded = ref(false)
const descriptionRef = ref<HTMLElement | null>(null)
const isDescriptionOverflowing = ref(false)

function checkOverflow() {
  if (descriptionRef.value) {
    isDescriptionOverflowing.value =
      descriptionRef.value.scrollHeight > descriptionRef.value.clientHeight
  }
}

onMounted(() => {
  nextTick(checkOverflow)
})

watch(description, () => {
  isDescriptionExpanded.value = false
  nextTick(checkOverflow)
})
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.vdp {
  padding: 8px 0;
  overflow-x: hidden;

  @include player-compact {
    padding: 4px 0;

    .vdp__section {
      padding: 8px 12px;
    }
  }

  // ── Sections ──
  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 16px;
    min-width: 0;

    & + & {
      padding-top: 16px;
    }
  }

  &__section-title {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    // text-faint (0.4 alpha) failed contrast on both themes for this small
    // uppercase header; tertiary (0.7) matches the settings panel's (~6:1+).
    color: var(--vp-text-tertiary);
    margin: 0;
  }

  &__section-subtitle {
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    // text-ghost (0.3 alpha) was ~2:1 in both themes.
    color: var(--vp-text-tertiary);
    margin: 6px 0 0;
  }

  // ── Title ──
  &__title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--vp-text-primary);
    margin: 0;
    line-height: 1.35;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @include player-compact {
      font-size: 1rem;
    }
  }

  // ── Badges ──
  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: var(--vp-text-tertiary);
    background: var(--vp-surface-hover);
    border: 1px solid var(--vp-border-default);
    border-radius: 6px;
    padding: 4px 10px;
  }

  &__badge-icon {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  // ── Description with collapse ──
  &__description-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__description {
    font-size: 0.8125rem;
    color: var(--vp-text-tertiary);
    margin: 0;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 6.2em;
    overflow: hidden;
    transition: max-height 0.3s ease;

    &--expanded {
      max-height: none;
    }
  }

  &__show-more {
    background: none;
    border: none;
    border-radius: 4px;
    color: var(--vp-text-primary);
    font-size: 0.75rem;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    padding: 4px 8px;
    margin-left: -8px;
    text-align: left;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  &__no-description {
    font-size: 0.8125rem;
    // text-ghost (0.3 alpha) was ~2:1 in both themes; the italic keeps it
    // reading as a placeholder.
    color: var(--vp-text-tertiary);
    margin: 0;
    font-style: italic;

    // A line saying nothing is a line of a short frame lost.
    @include player-compact {
      display: none;
    }
  }

  // ── Public metadata ──
  &__metadata {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0;
    padding-top: 4px;
  }

  // Label above value: a long value wraps under itself instead of under
  // the label.
  &__metadata-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__metadata-label {
    font-size: 0.75rem;
    font-weight: 500;
    // text-muted (0.5 alpha) fell under 4.5:1 on the light theme and over a
    // bright frame on the dark one.
    color: var(--vp-text-tertiary);
  }

  &__metadata-value {
    font-size: 0.8125rem;
    color: var(--vp-text-secondary);
    margin: 0;
    word-break: break-word;
  }

  // ── Card-style action rows ──
  &__card {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-width: 0;
    padding: 10px 12px;
    background: var(--vp-surface-subtle);
    border: 1px solid var(--vp-border-subtle);
    border-radius: 8px;
    color: var(--vp-text-secondary);
    cursor: pointer;
    text-align: left;
    text-decoration: none;
    font-family: inherit;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
      border-color: var(--vp-border-default);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }

    &--disabled {
      opacity: 0.35;
      cursor: default;
      pointer-events: none;
    }
  }

  &__card-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    background: var(--vp-border-subtle);
    border-radius: 6px;
  }

  &__card-icon {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }

  &__card-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  &__card-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--vp-text-primary);

    &--truncate {
      @include truncate-text;
    }
  }

  &__card-description {
    font-size: 0.75rem;
    // Same as the metadata label: text-muted fell under 4.5:1.
    color: var(--vp-text-tertiary);
  }

  &__card-arrow {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.3;
    transition: opacity 0.15s ease;

    .vdp__card:hover & {
      opacity: 0.6;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .vdp__description {
    transition: none;
  }
}
</style>
